import { Field, Input, Spinner, makeStyles } from '@fluentui/react-components';

import { TenantResolveState } from '../hooks/useTenantResolve';
import { IDENTIFIER } from '../theme/tokens';

const useStyles = makeStyles({
  // Resolved tenant GUID — tabular figures per the typography rule for
  // all identifiers (FLUENT_UI_ADDENDUM).
  resolvedId: {
    ...IDENTIFIER,
    fontSize: '12px',
  },
});

interface TenantResolveFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  state: TenantResolveState;
  disabled?: boolean;
  /** Defaults to true. Set false for an optional slot (the D2C destination). */
  required?: boolean;
  placeholder?: string;
}

// One T2T tenant slot: accepts a domain OR a raw tenant GUID in a single Fluent
// Field/Input, with inline resolve feedback carried on the Field's own
// validationState (the Fluent-native inline pattern — no toaster). Resolution
// itself is owned by useTenantResolve; this component only renders `state`.
export default function TenantResolveField({
  label,
  value,
  onChange,
  state,
  disabled,
  placeholder = 'Domain (e.g. contoso.com) or tenant ID',
  // FD-254: `required` was hardcoded. That was correct while every use was a
  // mandatory T2T slot, and became a BUG the moment D2C gained an OPTIONAL
  // destination: a Fluent `Field required` marks the input required, so an empty
  // one fails native form validation and the submit event never fires — the
  // button looks enabled, the click lands, and nothing happens. A D2C operator
  // could not create a migration at all. Defaults to true so every existing
  // caller is unchanged.
  required = true,
}: TenantResolveFieldProps) {
  const styles = useStyles();
  const feedback = deriveFeedback(state, styles.resolvedId);

  return (
    <Field
      label={label}
      required={required}
      validationState={feedback.validationState}
      validationMessage={feedback.validationMessage}
      hint={feedback.hint}
    >
      <Input
        value={value}
        onChange={(_, data) => onChange(data.value)}
        placeholder={placeholder}
        disabled={disabled}
        contentAfter={
          state.kind === 'resolving' ? (
            <Spinner size="tiny" aria-label="Resolving domain" />
          ) : undefined
        }
      />
    </Field>
  );
}

interface FieldFeedback {
  validationState: 'none' | 'success' | 'error' | 'warning';
  validationMessage?: string | JSX.Element;
  hint?: string;
}

function deriveFeedback(
  state: TenantResolveState,
  monoClass: string,
): FieldFeedback {
  switch (state.kind) {
    case 'idle':
      return { validationState: 'none' };
    case 'guid':
      return { validationState: 'none', hint: 'Using tenant ID directly.' };
    case 'resolving':
      return { validationState: 'none', hint: 'Resolving domain…' };
    case 'resolved':
      return {
        validationState: 'success',
        validationMessage: (
          <span>
            Resolved to <span className={monoClass}>{state.data.tenantId}</span>
          </span>
        ),
      };
    case 'error':
      return {
        validationState: 'error',
        validationMessage: errorCopy(state),
      };
  }
}

function errorCopy(state: Extract<TenantResolveState, { kind: 'error' }>): string {
  switch (state.code) {
    case 'INVALID_DOMAIN':
      return 'Enter a valid domain (e.g. contoso.com) or a tenant ID.';
    case 'TENANT_NOT_FOUND_FOR_DOMAIN':
      return 'No Entra tenant found for that domain.';
    case 'DISCOVERY_ERROR':
      return "Couldn't reach the tenant discovery service. Check the domain and try again.";
    default:
      return state.message || 'Could not resolve the domain.';
  }
}
